<div #cardRoot
  class="flex w-full flex-col overflow-hidden rounded-[calc(var(--radius)+8px)] border border-border bg-card [contain:layout_style_paint] [content-visibility:auto] [contain-intrinsic-size:auto_380px]">
  <div class="flex flex-col gap-5 p-5">

    <div class="flex flex-col gap-5 lg:grid lg:grid-cols-[minmax(0,1fr)_32%] lg:items-stretch lg:gap-x-6">

      <!-- Left: profile, stats, socials + CTA -->
      <div class="flex min-w-0 flex-col gap-5 lg:col-start-1 lg:min-h-full">

        <!-- Profile -->
        <div class="flex min-w-0 gap-4">
          <div (click)="launchPortfolio()" class="relative shrink-0 hover:cursor-pointer"
            [class]="compact ? 'w-20' : 'w-28'">
            <div class="relative aspect-square w-full overflow-visible rounded-xl bg-muted">
              <atom-ldg-img class="absolute top-0 left-0 size-full"
                [src]="imageService.getImageSource(creator.photoURL, 'gallery_lg')"
                inputClass="size-full object-cover rounded-xl" h="full" w="full" aspect="aspect-square" fit="cover"
                radius="md"></atom-ldg-img>
              @if (creator.isVerified) {
              <div
                class="absolute -bottom-1.5 -right-1.5 flex size-7 items-center justify-center rounded-full bg-primary">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"
                  class="size-4 text-white" aria-hidden="true">
                  <path d="M20 6 9 17l-5-5" />
                </svg>
              </div>
              }
            </div>
          </div>

          <div class="flex min-w-0 flex-1 flex-col gap-2">
            <h3 class="text-base font-semibold text-primary">{{ displayName() }}</h3>

            <div class="inline-flex flex-wrap items-center gap-x-2 gap-y-1">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                class="size-3.5 shrink-0 text-secondary" aria-hidden="true">
                <path
                  d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0" />
                <circle cx="12" cy="10" r="3" />
              </svg>
              <p class="m-0 text-sm text-secondary">{{ hometown() }}</p>
              <svg viewBox="0 0 6 6" aria-hidden="true" class="size-1.5 fill-secondary">
                <circle r="3" cx="3" cy="3" />
              </svg>
              <p class="m-0 text-sm text-secondary">{{ creatorAge }} years</p>
            </div>

            <div class="flex w-full flex-col items-start gap-1">
              <p #bioPreview
                class="m-0 line-clamp-3 w-full whitespace-pre-line text-sm text-secondary"
                [class.italic]="!hasRealBio()">{{ displayBio() }}</p>
              @if (hasRealBio() && bioIsTruncated()) {
              <button #bioMoreBtn type="button" (click)="toggleBioPopover($event)"
                class="m-0 cursor-pointer rounded-full border-0 bg-transparent p-0 text-xs font-medium text-accent hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                [attr.aria-expanded]="isBioPopoverOpen()" aria-haspopup="dialog" aria-label="Read full bio">
                Read more
              </button>
              }
            </div>

            @if (previewInterestLabels.length > 0) {
            <div class="flex flex-row flex-wrap items-center gap-1.5 sm:gap-2">
              @for (interestLabel of previewInterestLabels; track interestLabel) {
              <span
                class="inline-flex items-center rounded-full border border-primary/25 bg-primary/10 px-2.5 py-0.5 text-[10px] font-medium text-primary sm:text-xs">{{
                interestLabel }}</span>
              }
              @if (creator.interests.length > 3) {
              <div class="relative">
                <button #interestsMoreBtn type="button" (click)="toggleInterestsPopover($event)"
                  class="m-0 cursor-pointer rounded-full border-0 bg-transparent p-0 text-xs font-medium text-accent hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                  [attr.aria-expanded]="isInterestsPopoverOpen()" aria-haspopup="dialog"
                  [attr.aria-label]="'Show all ' + creator.interests.length + ' niches'">
                  +{{ creator.interests.length - 3 }} more
                </button>
              </div>
              }
            </div>
            }
          </div>
        </div>

        <!-- Stats row -->
        <dl class="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <div
            class="flex items-center gap-3 overflow-hidden rounded-[calc(var(--radius)+4px)] border border-border bg-[color-mix(in_srgb,var(--muted)_60%,var(--card))] px-4 py-3">
            <div class="flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary/15">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor"
                class="size-4 text-amber-400" aria-hidden="true">
                <path
                  d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
              </svg>
            </div>
            <div class="min-w-0">
              <dt class="truncate text-[10px] font-medium uppercase tracking-wide text-secondary">Rating</dt>
              @if (createdDaysAgo() && createdDaysAgo()! < 7) { <dd
                class="m-0 text-base font-semibold tracking-tight text-primary sm:text-lg">Unrated</dd>
                } @else {
                <dd class="m-0 text-base font-semibold tracking-tight text-primary sm:text-lg">
                  {{ creator.reviews_summary?.average_rating ?? 0 | number: "1.1" }}
                  @if (creator.reviews_summary && creator.reviews_summary.total_reviews) {
                  ({{ creator.reviews_summary.total_reviews }})
                  }
                </dd>
                }
            </div>
          </div>

          <div
            class="flex items-center gap-3 overflow-hidden rounded-[calc(var(--radius)+4px)] border border-border bg-[color-mix(in_srgb,var(--muted)_60%,var(--card))] px-4 py-3">
            <div class="flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary/15">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                class="size-4 text-primary" aria-hidden="true">
                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
            </div>
            <div class="min-w-0">
              <dt class="truncate text-[10px] font-medium uppercase tracking-wide text-secondary">Total Followers</dt>
              <dd class="m-0 text-base font-semibold tracking-tight text-primary sm:text-lg">
                {{ totalFollowersDisplay }}
              </dd>
            </div>
          </div>

          @if (platformStatRows(); as rows) {
          @if (rows.length > 0) {
          <div
            class="col-span-2 flex flex-col gap-2 overflow-hidden rounded-[calc(var(--radius)+4px)] border border-border bg-[color-mix(in_srgb,var(--muted)_60%,var(--card))] px-4 py-3">
            <dt class="truncate text-[10px] font-medium uppercase tracking-wide text-secondary">Platform stats</dt>
            <dd class="m-0 flex flex-col gap-1.5">
              @for (row of rows; track row.platform) {
              <div class="flex items-center justify-between gap-2">
                <span class="inline-flex min-w-0 items-center gap-1.5">
                  <img [src]="row.iconSrc" alt="" class="size-3.5" aria-hidden="true" />
                  <p class="m-0 text-sm font-medium text-primary">{{ row.label }}</p>
                </span>
                <span class="inline-flex shrink-0 flex-wrap items-center justify-end gap-x-2 gap-y-0.5">
                  @for (metric of row.metrics; track metric.key) {
                  @if (metric.format === 'percent') {
                  <p class="m-0 text-sm" [class.font-semibold]="$first" [class.text-primary]="$first"
                    [class.text-secondary]="!$first">{{ metric.value | number:'1.1-2' }}%</p>
                  } @else {
                  <p class="m-0 text-sm" [class.font-semibold]="$first" [class.text-primary]="$first"
                    [class.text-secondary]="!$first">{{ formatService.convertIntToString(metric.value) }}
                    {{ metric.label }}</p>
                  }
                  }
                </span>
              </div>
              }
            </dd>
          </div>
          }
          }
        </dl>

        <!-- Socials + CTA -->
        <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between lg:mt-auto">

          <!-- Ultra - Social Accounts -->
          @if (creator.creatorSocialAccounts && creator.creatorSocialAccounts.length > 0) {
          <div class="flex flex-row gap-x-2" *appHasRole="[77]">
            @for (socialAccount of creator.creatorSocialAccounts; track $index) {
            <span
              class="inline-flex items-center gap-x-1.5 rounded-md px-2 py-1 text-sm font-medium text-gray-900 inset-ring inset-ring-gray-200 dark:text-white dark:inset-ring-white/10">
              @switch (socialAccount.platform) {
              @case ('tiktok') {
              <svg viewBox="0 0 24 24" fill="currentColor" role="img" xmlns="http://www.w3.org/2000/svg"
                class="block size-3.5 shrink-0 text-primary sm:size-4" aria-hidden="true">
                <title>TikTok</title>
                <path
                  d="M12.53.02C13.84 0 15.14.01 16.44 0c.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
              </svg>
              }
              @case ('instagram') {
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                class="block size-3.5 shrink-0 text-primary sm:size-4" aria-hidden="true">
                <rect width="20" height="20" x="2" y="2" rx="5" ry="5" />
                <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
                <line x1="17.5" x2="17.51" y1="6.5" y2="6.5" />
              </svg>
              }
              @case ('youtube') {
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                class="block size-3.5 shrink-0 text-primary sm:size-4" aria-hidden="true">
                <path
                  d="M2.5 17a24.12 24.12 0 0 1 0-10 2 2 0 0 1 1.4-1.4 49.56 49.56 0 0 1 16.2 0A2 2 0 0 1 21.5 7a24.12 24.12 0 0 1 0 10 2 2 0 0 1-1.4 1.4 49.55 49.55 0 0 1-16.2 0A2 2 0 0 1 2.5 17" />
                <path d="m10 15 5-3-5-3z" />
              </svg>
              }
              @case ('snapchat') {
              <svg viewBox="0 0 24 24" fill="currentColor" stroke-width="2" role="img"
                xmlns="http://www.w3.org/2000/svg" class="block size-3.5 shrink-0 text-primary sm:size-4"
                aria-hidden="true">
                <path
                  d="M21.9756 17.1497C21.8884 16.8566 21.4712 16.6499 21.4712 16.6499C21.4317 16.6288 21.3965 16.6099 21.3674 16.5951C20.672 16.2535 20.0555 15.8423 19.5365 15.3762C19.1193 15.0008 18.7623 14.5875 18.4758 14.1489C18.125 13.6132 17.961 13.1662 17.8904 12.9237C17.851 12.7655 17.8572 12.7022 17.8904 12.62C17.9174 12.5504 17.9984 12.485 18.0357 12.4534C18.2703 12.2847 18.6481 12.0358 18.8806 11.884C19.0819 11.7512 19.2542 11.6373 19.3559 11.5656C19.6818 11.3336 19.906 11.0974 20.0368 10.8423C20.207 10.5133 20.2278 10.1506 20.0949 9.79418C19.9164 9.31337 19.4743 9.02657 18.9117 9.02657C18.7872 9.02657 18.6585 9.04133 18.5298 9.06875C18.208 9.14045 17.9029 9.25643 17.6475 9.35765C17.6289 9.36609 17.6081 9.35133 17.6102 9.33024C17.6372 8.68705 17.6683 7.82244 17.5977 7.00211C17.5355 6.25981 17.3839 5.63349 17.139 5.0873C16.892 4.53901 16.5702 4.13412 16.319 3.841C16.0803 3.56263 15.6589 3.15141 15.0237 2.78237C14.1311 2.2636 13.114 2 12.0013 2C10.8908 2 9.87572 2.2636 8.98105 2.78237C8.30849 3.1725 7.87879 3.61324 7.68367 3.841C7.4325 4.13412 7.11075 4.53901 6.86372 5.0873C6.6167 5.63349 6.46724 6.2577 6.40497 7.00211C6.33439 7.82666 6.36345 8.62168 6.39252 9.33024C6.39252 9.35133 6.37383 9.36609 6.35308 9.35765C6.09775 9.25643 5.79261 9.14045 5.47086 9.06875C5.34423 9.04133 5.21553 9.02657 5.08891 9.02657C4.52844 9.02657 4.08629 9.31337 3.9057 9.79418C3.77285 10.1506 3.7936 10.5133 3.96382 10.8423C4.09667 11.0974 4.31878 11.3336 4.64469 11.5656C4.74432 11.6373 4.91869 11.7512 5.12005 11.884C5.34631 12.0337 5.71373 12.2763 5.95037 12.4429C5.97943 12.4639 6.07907 12.5399 6.11021 12.62C6.14342 12.7043 6.14965 12.7676 6.10605 12.9363C6.0334 13.1809 5.86941 13.6238 5.52483 14.1489C5.23837 14.5896 4.88133 15.0008 4.46409 15.3762C3.94514 15.8423 3.32862 16.2535 2.63323 16.5951C2.60002 16.612 2.56057 16.631 2.51906 16.6563C2.51906 16.6563 2.1039 16.8714 2.02502 17.1497C1.90877 17.5609 2.21807 17.9469 2.53151 18.1535C3.04631 18.4909 3.67321 18.6723 4.03647 18.7714C4.13819 18.7988 4.22952 18.8241 4.31256 18.8494C4.36445 18.8663 4.49523 18.9169 4.55127 18.9907C4.62185 19.0835 4.63016 19.1974 4.65506 19.326C4.69451 19.5432 4.78377 19.811 5.04739 19.9966C5.33801 20.1991 5.70542 20.2138 6.17248 20.2328C6.6603 20.2518 7.26643 20.275 7.96183 20.5091C8.28358 20.6166 8.57627 20.8001 8.91255 21.011C9.61832 21.4517 10.4964 22 11.9951 22C13.4959 22 14.3802 21.4496 15.0901 21.0067C15.4264 20.798 15.715 20.6166 16.0305 20.5112C16.7259 20.2771 17.332 20.2539 17.8198 20.2349C18.2869 20.2159 18.6543 20.2033 18.9449 19.9987C19.2272 19.8005 19.3082 19.5053 19.3456 19.2838C19.3663 19.1742 19.3788 19.0751 19.441 18.9949C19.495 18.9253 19.6154 18.8768 19.6715 18.8579C19.7566 18.8305 19.8521 18.8051 19.9579 18.7756C20.3212 18.6765 20.7779 18.5605 21.3321 18.2421C22.0005 17.8562 22.0462 17.3838 21.9756 17.1497Z"
                  stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
              }
              }
              <svg viewBox="0 0 6 6" aria-hidden="true" class="size-1.5 fill-yellow-500 dark:fill-yellow-400">
                <circle r="3" cx="3" cy="3" />
              </svg>
              {{ formatService.convertIntToString(socialAccount.followerCount) }}
            </span>
            }
          </div>
          }
          <!-- Ultra - Social Accounts - END -->

          <div class="flex flex-row flex-wrap gap-2">
            @if (creator.tikTokFollowers > 0 || !!creator.tikTokHandle) {
            <button type="button"
              class="inline-flex items-center gap-1.5 rounded-[calc(var(--radius)+2px)] bg-muted px-3 py-2 text-base font-medium leading-4 tracking-tight text-muted-foreground sm:text-lg sm:leading-[1.125rem]"
              (click)="openSocialProfile('tiktok')" aria-label="Open TikTok on profile">
              <svg viewBox="0 0 24 24" fill="currentColor" role="img" xmlns="http://www.w3.org/2000/svg"
                class="block size-3.5 shrink-0 text-primary sm:size-4" aria-hidden="true">
                <title>TikTok</title>
                <path
                  d="M12.53.02C13.84 0 15.14.01 16.44 0c.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
              </svg>
              <span class="block leading-4 sm:leading-[1.125rem]">{{ creator.tikTokFollowers > 0 ?
                formatService.convertIntToString(creator.tikTokFollowers) : '0' }}</span>
            </button>
            }

            @if (creator.instaFollowers > 0 || !!creator.instaHandle) {
            <button type="button"
              class="inline-flex items-center gap-1.5 rounded-[calc(var(--radius)+2px)] bg-muted px-3 py-2 text-base font-medium leading-4 tracking-tight text-muted-foreground sm:text-lg sm:leading-[1.125rem]"
              (click)="openSocialProfile('instagram')" aria-label="Open Instagram on profile">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                class="block size-3.5 shrink-0 text-primary sm:size-4" aria-hidden="true">
                <rect width="20" height="20" x="2" y="2" rx="5" ry="5" />
                <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
                <line x1="17.5" x2="17.51" y1="6.5" y2="6.5" />
              </svg>
              <span class="block leading-4 sm:leading-[1.125rem]">{{ creator.instaFollowers > 0 ?
                formatService.convertIntToString(creator.instaFollowers) : '0' }}</span>
            </button>
            }

            @if (creator.snapchatFollowers > 0 || !!creator.snapchatHandle) {
            <button type="button"
              class="inline-flex items-center gap-1.5 rounded-[calc(var(--radius)+2px)] bg-muted px-3 py-2 text-base font-medium leading-4 tracking-tight text-muted-foreground sm:text-lg sm:leading-[1.125rem]"
              (click)="openSocialProfile('snapchat')" aria-label="Open Snapchat on profile">
              <svg viewBox="0 0 24 24" fill="currentColor" stroke-width="2" role="img"
                xmlns="http://www.w3.org/2000/svg" class="block size-3.5 shrink-0 text-primary sm:size-4"
                aria-hidden="true">
                <path
                  d="M21.9756 17.1497C21.8884 16.8566 21.4712 16.6499 21.4712 16.6499C21.4317 16.6288 21.3965 16.6099 21.3674 16.5951C20.672 16.2535 20.0555 15.8423 19.5365 15.3762C19.1193 15.0008 18.7623 14.5875 18.4758 14.1489C18.125 13.6132 17.961 13.1662 17.8904 12.9237C17.851 12.7655 17.8572 12.7022 17.8904 12.62C17.9174 12.5504 17.9984 12.485 18.0357 12.4534C18.2703 12.2847 18.6481 12.0358 18.8806 11.884C19.0819 11.7512 19.2542 11.6373 19.3559 11.5656C19.6818 11.3336 19.906 11.0974 20.0368 10.8423C20.207 10.5133 20.2278 10.1506 20.0949 9.79418C19.9164 9.31337 19.4743 9.02657 18.9117 9.02657C18.7872 9.02657 18.6585 9.04133 18.5298 9.06875C18.208 9.14045 17.9029 9.25643 17.6475 9.35765C17.6289 9.36609 17.6081 9.35133 17.6102 9.33024C17.6372 8.68705 17.6683 7.82244 17.5977 7.00211C17.5355 6.25981 17.3839 5.63349 17.139 5.0873C16.892 4.53901 16.5702 4.13412 16.319 3.841C16.0803 3.56263 15.6589 3.15141 15.0237 2.78237C14.1311 2.2636 13.114 2 12.0013 2C10.8908 2 9.87572 2.2636 8.98105 2.78237C8.30849 3.1725 7.87879 3.61324 7.68367 3.841C7.4325 4.13412 7.11075 4.53901 6.86372 5.0873C6.6167 5.63349 6.46724 6.2577 6.40497 7.00211C6.33439 7.82666 6.36345 8.62168 6.39252 9.33024C6.39252 9.35133 6.37383 9.36609 6.35308 9.35765C6.09775 9.25643 5.79261 9.14045 5.47086 9.06875C5.34423 9.04133 5.21553 9.02657 5.08891 9.02657C4.52844 9.02657 4.08629 9.31337 3.9057 9.79418C3.77285 10.1506 3.7936 10.5133 3.96382 10.8423C4.09667 11.0974 4.31878 11.3336 4.64469 11.5656C4.74432 11.6373 4.91869 11.7512 5.12005 11.884C5.34631 12.0337 5.71373 12.2763 5.95037 12.4429C5.97943 12.4639 6.07907 12.5399 6.11021 12.62C6.14342 12.7043 6.14965 12.7676 6.10605 12.9363C6.0334 13.1809 5.86941 13.6238 5.52483 14.1489C5.23837 14.5896 4.88133 15.0008 4.46409 15.3762C3.94514 15.8423 3.32862 16.2535 2.63323 16.5951C2.60002 16.612 2.56057 16.631 2.51906 16.6563C2.51906 16.6563 2.1039 16.8714 2.02502 17.1497C1.90877 17.5609 2.21807 17.9469 2.53151 18.1535C3.04631 18.4909 3.67321 18.6723 4.03647 18.7714C4.13819 18.7988 4.22952 18.8241 4.31256 18.8494C4.36445 18.8663 4.49523 18.9169 4.55127 18.9907C4.62185 19.0835 4.63016 19.1974 4.65506 19.326C4.69451 19.5432 4.78377 19.811 5.04739 19.9966C5.33801 20.1991 5.70542 20.2138 6.17248 20.2328C6.6603 20.2518 7.26643 20.275 7.96183 20.5091C8.28358 20.6166 8.57627 20.8001 8.91255 21.011C9.61832 21.4517 10.4964 22 11.9951 22C13.4959 22 14.3802 21.4496 15.0901 21.0067C15.4264 20.798 15.715 20.6166 16.0305 20.5112C16.7259 20.2771 17.332 20.2539 17.8198 20.2349C18.2869 20.2159 18.6543 20.2033 18.9449 19.9987C19.2272 19.8005 19.3082 19.5053 19.3456 19.2838C19.3663 19.1742 19.3788 19.0751 19.441 18.9949C19.495 18.9253 19.6154 18.8768 19.6715 18.8579C19.7566 18.8305 19.8521 18.8051 19.9579 18.7756C20.3212 18.6765 20.7779 18.5605 21.3321 18.2421C22.0005 17.8562 22.0462 17.3838 21.9756 17.1497Z"
                  stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
              <span class="block leading-4 sm:leading-[1.125rem]">{{ creator.snapchatFollowers > 0 ?
                formatService.convertIntToString(creator.snapchatFollowers) : '0' }}</span>
            </button>
            }

            @if (creator.youtubeSubscribers > 0 || !!creator.youtubeHandle) {
            <button type="button"
              class="inline-flex items-center gap-1.5 rounded-[calc(var(--radius)+2px)] bg-muted px-3 py-2 text-base font-medium leading-4 tracking-tight text-muted-foreground sm:text-lg sm:leading-[1.125rem]"
              (click)="openSocialProfile('youtube')" aria-label="Open YouTube on profile">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                class="block size-3.5 shrink-0 text-primary sm:size-4" aria-hidden="true">
                <path
                  d="M2.5 17a24.12 24.12 0 0 1 0-10 2 2 0 0 1 1.4-1.4 49.56 49.56 0 0 1 16.2 0A2 2 0 0 1 21.5 7a24.12 24.12 0 0 1 0 10 2 2 0 0 1-1.4 1.4 49.55 49.55 0 0 1-16.2 0A2 2 0 0 1 2.5 17" />
                <path d="m10 15 5-3-5-3z" />
              </svg>
              <span class="block leading-4 sm:leading-[1.125rem]">{{ creator.youtubeSubscribers > 0 ?
                formatService.convertIntToString(creator.youtubeSubscribers) : '0' }}</span>
            </button>
            }
          </div>

          @if (!compact) {
          <div class="flex shrink-0 flex-wrap items-end justify-end gap-2">
            <button type="button" (click)="launchPortfolio()"
              class="btn-shout btn-shout-sm btn-shout-secondary">
              Profile
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="size-5"
                aria-hidden="true">
                <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
            </button>
            @if (!monitoringBrief) {
            <div class="flex flex-col items-center gap-1">
              @if (isInAnyGroup()) {
              <p
                class="m-0 inline-flex items-center gap-1 rounded-full border border-green-300 bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700 dark:border-green-700 dark:bg-green-900/30 dark:text-green-400">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"
                  class="size-3" aria-hidden="true">
                  <path d="M20 6 9 17l-5-5" />
                </svg>
                {{ inGroupBadgeLabel() }}
              </p>
              }
              <button type="button" (click)="openAddToGroup()"
                class="btn-shout btn-shout-sm btn-shout-secondary"
                [attr.aria-label]="isInAnyGroup() ? 'Add to group. This creator is already in a group' : 'Add to group'">
                Add to Group
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="size-5"
                  aria-hidden="true">
                  <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
              </button>
            </div>
            @if (directInvite) {
            @if (inviteSending()) {
            <button type="button" disabled class="btn-shout btn-shout-sm btn-shout-secondary">
              Inviting...
              <svg aria-hidden="true"
                class="size-5 animate-spin text-gray-200 fill-primary dark:text-gray-300"
                viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                  fill="currentColor" />
                <path
                  d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                  fill="currentFill" />
              </svg>
            </button>
            } @else if (creator.briefApplication) {
            <atom-application-status-badge
              [applicationStatus]="creator.briefApplication.applicationStatus"></atom-application-status-badge>
            } @else {
            <button type="button" (click)="inviteNow()"
              class="btn-shout btn-shout-sm btn-shout-primary">
              Invite Now
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="size-5"
                aria-hidden="true">
                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <line x1="19" x2="19" y1="8" y2="14" />
                <line x1="22" x2="16" y1="11" y2="11" />
              </svg>
            </button>
            }
            } @else if (existingApplicationStatus(); as status) {
            <atom-application-status-badge [applicationStatus]="status"
              title="This creator already has an application on this brief"></atom-application-status-badge>
            } @else {
            @if (!isCreatorSelected()) {
            <button type="button" (click)="onCollaborate($event)"
              class="btn-shout btn-shout-sm btn-shout-primary">
              Invite to a brief
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="size-5"
                aria-hidden="true">
                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <line x1="19" x2="19" y1="8" y2="14" />
                <line x1="22" x2="16" y1="11" y2="11" />
              </svg>
            </button>
            } @else {
            <button type="button" (click)="collaboratorsService.removeCreator(creator)"
              class="btn-shout btn-shout-sm btn-shout-alert">
              Un-Select
            </button>
            }
            }
            } @else if (monitoringBrief) {
            @if (inviteSending()) {
            <button type="button" disabled class="btn-shout btn-shout-sm btn-shout-secondary">
              Loading...
              <svg aria-hidden="true"
                class="size-5 animate-spin text-gray-200 fill-primary dark:text-gray-300"
                viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                  fill="currentColor" />
                <path
                  d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                  fill="currentFill" />
              </svg>
            </button>
            } @else if (creator.socialMonitoringInquiry) {
            <span
              class="inline-flex items-center gap-x-1.5 rounded-full bg-lime-100 px-2 py-1 text-xs font-medium text-lime-700 dark:bg-lime-400/10 dark:text-lime-400">
              <svg viewBox="0 0 6 6" aria-hidden="true" class="size-1.5 fill-lime-500 dark:fill-lime-400">
                <circle r="3" cx="3" cy="3" />
              </svg>
              {{ creator.socialMonitoringInquiry.inquiryStatus }}
            </span>
            } @else {
            <button type="button" (click)="addToMonitoring()"
              class="btn-shout btn-shout-sm btn-shout-primary">
              Monitor Now
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="size-5"
                aria-hidden="true">
                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <line x1="19" x2="19" y1="8" y2="14" />
                <line x1="22" x2="16" y1="11" y2="11" />
              </svg>
            </button>
            }
            }
          </div>
          }
        </div>

      </div>

      @if (!compact) {
      <div class="flex w-full min-w-0 shrink-0 flex-col gap-2 lg:col-start-2">
        <h4 class="flex shrink-0 items-center gap-2 text-[10px] font-semibold uppercase tracking-wide text-secondary">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
            class="size-3.5 text-secondary" aria-hidden="true">
            <path d="M2 7v10" />
            <path d="M6 5v14" />
            <rect width="12" height="18" x="10" y="3" rx="2" />
          </svg>
          Portfolio Content
        </h4>
        <div class="grid grid-cols-3 gap-2">
          @if (!isInView() || portfolioLoading()) {
          @for (placeholder of portfolioPlaceholders; track placeholder) {
          <div class="aspect-9/16 w-full rounded-lg bg-muted" aria-hidden="true"></div>
          }
          } @else {
          @for (video of portfolioPreviewVideos(); track video.id) {
          <div (click)="previewVideo(video)"
            class="aspect-9/16 w-full overflow-hidden rounded-lg hover:cursor-pointer hover:ring-2 hover:ring-primary/50 [&_atom-video-thumbnail]:block [&_atom-video-thumbnail]:size-full [&_atom-video-thumbnail]:object-cover [&_img]:block [&_img]:size-full [&_img]:object-cover">
            @if (hasPortfolioThumbnail(video)) {
            <img [src]="video.thumbnailURL!" alt="" loading="lazy" decoding="async" class="size-full object-cover" />
            } @else {
            <atom-video-thumbnail [videoUrl]="video.fullUrl" [thumbnailURL]="video.thumbnailURL ?? undefined"
              [videoFormat]="VideoFormatEnum.Vertical" [thumb_cdn_size]="'gallery'"></atom-video-thumbnail>
            }
          </div>
          }
          }
        </div>
      </div>
      }

    </div>
  </div>
</div>

@if (showAddToGroup()) {
<ambala-groups [creator]="creator" (closed)="closeAddToGroup()"></ambala-groups>
}

@if (isBioPopoverOpen()) {
<div #bioPopoverPanel (click)="onBioPopoverPanelClick($event)" role="dialog" aria-label="Creator bio"
  class="fixed z-[200] w-[min(65.5rem,calc(100vw-1rem))] max-h-[min(32rem,calc(100dvh-1rem))] overflow-y-auto rounded-xl border border-border bg-popover p-4 text-popover-foreground shadow-lg"
  [style.top]="bioPopoverStyle()?.top" [style.left]="bioPopoverStyle()?.left">
  <p class="m-0 mb-2 text-[10px] font-semibold uppercase tracking-wide text-secondary">Bio</p>
  <p class="m-0 whitespace-pre-wrap text-sm leading-relaxed text-secondary">{{ creator.bio }}</p>
</div>
}

@if (isInterestsPopoverOpen()) {
<div #interestsPopoverPanel (click)="onInterestsPopoverPanelClick($event)" role="dialog" aria-label="All niches"
  class="fixed z-[200] w-[22rem] max-h-[min(24rem,calc(100dvh-1rem))] max-w-[calc(100vw-1rem)] overflow-y-auto rounded-xl border border-border bg-popover p-3 text-popover-foreground shadow-lg sm:w-96"
  [style.top]="interestsPopoverStyle()?.top" [style.left]="interestsPopoverStyle()?.left">
  <p class="m-0 mb-2 text-[10px] font-semibold uppercase tracking-wide text-secondary">All niches</p>
  <div class="flex flex-wrap gap-1.5">
    @for (label of getAllInterestLabels(); track label) {
    <span
      class="inline-flex items-center rounded-full border border-primary/25 bg-primary/10 px-2.5 py-0.5 text-[10px] font-medium text-primary sm:text-xs">{{
      label }}</span>
    }
  </div>
</div>
}